import { z } from 'zod';

export const importOrderSchema = z.object({
  supplierId: z.string().min(1, 'Selecciona un proveedor'),
  orderDate: z.string().min(1, 'Fecha de orden requerida'),
  estimatedShipDate: z.string().optional(),
  estimatedArrivalDate: z.string().optional(),
  incoterm: z.enum(['EXW', 'FCA', 'FAS', 'FOB', 'CFR', 'CIF', 'CPT', 'CIP', 'DAP', 'DPU', 'DDP']),
  paymentTerms: z.string().optional(),
  transportMode: z.enum(['SEA', 'AIR', 'LAND', 'RAIL', 'MULTIMODAL']).optional().default('SEA'),
  originPort: z.string().optional(),
  destinationPort: z.string().optional(),
  currency: z.string().optional(),
  notes: z.string().optional(),
});

export const importItemSchema = z.object({
  materialId: z.string().optional(),
  description: z.string().min(1, 'Descripción requerida'),
  hsCode: z.string().optional(),
  quantity: z.number().positive('Cantidad debe ser mayor a 0'),
  unitOfMeasure: z.string().min(1, 'Unidad de medida requerida'),
  unitFobPrice: z.number().nonnegative('Precio FOB debe ser mayor o igual a 0'),
  weightKg: z.number().optional(),
  volumeCbm: z.number().optional(),
  dutyRate: z.number().min(0).max(1).optional(),
});

export const trackingEventSchema = z.object({
  status: z.enum([
    'DRAFT',
    'CONFIRMED',
    'SHIPPED',
    'IN_TRANSIT',
    'CUSTOMS',
    'CUSTOMS_HOLD',
    'DELIVERED',
    'CANCELLED',
  ]),
  title: z.string().min(1, 'Título requerido'),
  description: z.string().optional(),
  location: z.string().optional(),
  eventDate: z.string().min(1, 'Fecha requerida'),
});

export const supplierSchema = z.object({
  name: z.string().min(1, 'Nombre requerido'),
  country: z.string().min(1, 'País requerido'),
  contactName: z.string().optional(),
  contactEmail: z.string().email('Email inválido').optional().or(z.literal('')),
  contactPhone: z.string().optional(),
  website: z.string().url('URL inválida').optional().or(z.literal('')),
});

export const landedCostSchema = z.object({
  fobPrice: z.number().positive('Precio FOB debe ser mayor a 0'),
  weightKg: z.number().nonnegative().optional(),
  volumeCbm: z.number().nonnegative().optional(),
  hsCode: z.string().min(1, 'Código HS requerido'),
  originCountry: z.string().min(2, 'País de origen requerido'),
  destinationCountry: z.string().min(2, 'País de destino requerido'),
  incoterm: z.enum(['EXW', 'FCA', 'FAS', 'FOB', 'CFR', 'CIF', 'CPT', 'CIP', 'DAP', 'DPU', 'DDP']),
  insuranceRate: z.number().min(0).max(1).optional(),
  transportMode: z.enum(['SEA', 'AIR', 'LAND', 'RAIL', 'MULTIMODAL']).optional().default('SEA'),
});

export const containerSchema = z.object({
  containerTypeId: z.string().min(1, 'Seleccione un tipo de contenedor'),
  containerNumber: z.string().optional(),
  sealNumber: z.string().optional(),
  originPort: z.string().optional(),
  destinationPort: z.string().optional(),
  etd: z.string().optional(),
  eta: z.string().optional(),
});

export const containerItemSchema = z.object({
  materialId: z.string().optional(),
  description: z.string().min(1, 'La descripción es requerida'),
  lengthCm: z.coerce.number().positive('El largo debe ser positivo'),
  widthCm: z.coerce.number().positive('El ancho debe ser positivo'),
  heightCm: z.coerce.number().positive('La altura debe ser positiva'),
  weightKg: z.coerce.number().positive('El peso debe ser positivo'),
  quantity: z.coerce.number().int().min(1, 'La cantidad minima es 1'),
  color: z.string().optional(),
});

export type ImportOrderFormData = z.infer<typeof importOrderSchema>;
export type ImportItemFormData = z.infer<typeof importItemSchema>;
export type TrackingEventFormData = z.infer<typeof trackingEventSchema>;
export type SupplierFormData = z.infer<typeof supplierSchema>;
export type LandedCostFormData = z.infer<typeof landedCostSchema>;
export type ContainerFormData = z.infer<typeof containerSchema>;
export type ContainerItemFormData = z.infer<typeof containerItemSchema>;
